/* eslint-disable @typescript-eslint/no-unused-vars */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { UsersApi } from '../apis';
import { EmployeeContext } from '../contexts';
import { IDelegateOption } from '../../config/interfaces';

/**
 * Redux slice for managing employee-related state using Redux Toolkit's `createSlice`.
 *
 * This slice handles actions related to employee management, department selection, employee search,
 * employee notes, delegation, and support/validation employee lists, including:
 * - Setting and clearing departments and selected departments
 * - Managing loading states for all asynchronous employee operations
 *
 * @property {object} initialState - The initial state for the employee context.
 * @property {Function} setSelectedDepts - Sets the selected departments.
 * @property {Function} clearAllDepts - Clears all departments and selected departments.
 * @property {Function} setDelegatedEmployee - Sets the selected delegated employee.
 *
 * @example
 * dispatch(setSelectedDepts(['Cardiology']));
 * dispatch(clearAllDepts());
 *
 * @returns {object} The employee slice reducer and actions.
 */
const employeeSlice = createSlice({
  name: 'employee',
  initialState: EmployeeContext,
  reducers: {
    setSelectedDepts: (state, action: PayloadAction<string[]>) => {
      return {
        ...state,
        selectedDepts: action.payload
      };
    },
    clearAllDepts: state => {
      return {
        ...state,
        depts: [],
        selectedDepts: []
      };
    },
    setDelegatedEmployee: (state, action: PayloadAction<IDelegateOption>) => {
      return {
        ...state,
        selectedDelegatedEmployee: action.payload
      };
    },
    setShiftStatusData: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        isShiftStatusDataAvailable: action.payload
      };
    },
    setEmployeeShiftStatusFilter: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        shiftStatusFilter: action.payload
      };
    },
    clearEmployeeShiftStatusFilter: state => {
      return {
        ...state,
        shiftStatusFilter: ''
      };
    }
  },
  extraReducers: builder => {
    builder.addMatcher(
      UsersApi.endpoints.getSupportSubjectsToRound.matchFulfilled,
      (state, action) => ({
        ...state,
        depts: [
          ...new Set(
            action.payload?.map(
              employee =>
                ((employee.adHoc
                  ? employee?.employeeDept
                  : employee?.dept) as string) || 'Unknown'
            ) ?? []
          )
        ]
      })
    );
    builder.addMatcher(
      UsersApi.endpoints.getSupportSubjectsToRound.matchRejected,
      state => ({
        ...state,
        depts: []
      })
    );
  }
});

export const {
  setSelectedDepts,
  clearAllDepts,
  setDelegatedEmployee,
  setShiftStatusData,
  setEmployeeShiftStatusFilter,
  clearEmployeeShiftStatusFilter
} = employeeSlice.actions;

export default employeeSlice.reducer;
